import { cx } from '@linaria/core';
import { useRef, useId, useState } from 'react';
import { getCaptionValue } from '@pm2/content-engine';
import { useCourseRevisionState } from '@pm2/course-revisions';
import { pm2Surfaces } from '@pm2/design-tokens';
import { pm2ConfigStore } from '@pm2/store';
import { configureIframeExport } from '@provider/app-context';
import { resets } from '@ui/css';
import { Tooltip } from '@ui/kit';
import { CrossIcon } from '@ui/kit/icons';
import { SizeTransition, Chevron, CSSRefTransition, Throbber } from '@utils/react';
import { useResolvedDisplayData } from '../../hook.resolve-display-data.js';
import { headingCls, headingContentCls, badgeWrapperCls, badgeImageCls, indexBadgeCls, headerWrapperCls, headerTextCls, scrambledCls, descriptionWrapperCls, CONTENT_TRANSITION_DURATION_MS, descriptionCls, descriptionPanelCls, descriptionToggleCls, descriptionPanelWrapperCls, expandCls, iconWrapperCls, chevronCls, crossCls, headerButtonCls, headingWrapperCls, FADE_TRANSITION_DURATION_MS, fadeCls, iframeCls, throbberCls, textCls, retryButtonCls, contentContainerCls, captionCls, wrapperCls } from './comp.harmonica-iframe.style.js';
import { useExpandableIframe } from './hook.expandable.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

const HarmonicaIframeComponentSelector = "harmonica-iframe-component";
/**
 * Renders an iframe inside an accordion which fills the entire viewport
 * excluding the header when expanded.
 */
const HarmonicaIframeComponent = props => {
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const loginDisabled = pm2ConfigStore.useSelector(state => state.config.settings.loginDisabled);
  const badgeImage = pm2ConfigStore.useSelector(state => state.config.theme.componentPreviewImage);
  const isEditingRevision = useCourseRevisionState(state => state.isEditing);
  const ref = useRef(null);
  const id = useId();
  const buttonId = `iframeButton-${id}`;
  const sectionId = `iframeSection-${id}`;
  const rootRef = useRef(null);
  const [isExpanding, isExpanded, isCollapsing,, showIframe, toggleIframe] = useExpandableIframe(rootRef, isEditingRevision);
  const [displayData, failedLoadingSrc, retryFetchingSrc] = useResolvedDisplayData(props.getDisplayData, props.learningObject?.id);
  const [loaded, setLoaded] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const hasDescription = Array.isArray(props.richTextDescription) ? props.richTextDescription.length > 0 : Boolean(props.richTextDescription);

  // We cannot render iframes that requires authentication if login has been
  // disabled.
  const isIframeDisabled = props.isScrambled || loginDisabled && !!displayData?.src && /userid/gi.test(displayData.src);

  // The throbber should only be visible as long as nothing has gone wrong and
  // if the iframe hasn't been loaded yet
  const showThrobber = isExpanded && !loaded && !failedLoadingSrc && !isIframeDisabled;
  const onIframeLoaded = () => {
    setLoaded(true);
    configureIframeExport(ref, pm2Surfaces.primaryWithCustomColor.default);
  };
  const onHeaderClick = () => {
    if (!isExpanded) {
      toggleIframe();
    }
    setShowDescription(false);
  };
  const onCloseClick = () => {
    if (isExpanded) {
      toggleIframe();
    }
  };
  const toggleDescription = evt => {
    evt.stopPropagation();
    evt.preventDefault();
    setShowDescription(!showDescription);
  };
  return /*#__PURE__*/jsxs("section", {
    ref: rootRef,
    "data-component-type": HarmonicaIframeComponentSelector,
    className: cx(wrapperCls, resets.rootCls, isEditingRevision && "disabled", isExpanding && "expanding", isExpanded && "expanded", isCollapsing && "collapsing", !!props.richTextDescription && "hasDescription", props.isScrambled && "scrambled"),
    children: [/*#__PURE__*/jsx(Tooltip, {
      disabled: !isIframeDisabled || props.isScrambled,
      placement: "top",
      body: "Denne funktion kan ikke tilg\xE5s i \xF8jeblikket, da vi har problemer med login.",
      ariaType: "description",
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: cx(headingWrapperCls, isIframeDisabled && "disabled", isExpanded && "expanded"),
        children: /*#__PURE__*/jsx("h3", {
          className: headingCls,
          children: /*#__PURE__*/jsx("div", {
            className: headingContentCls,
            children: /*#__PURE__*/jsxs("button", {
              id: buttonId,
              className: cx(headerButtonCls, isExpanding && "expanding", isExpanded && "expanded", !!badgeImage && "hasBadge"),
              "aria-controls": sectionId,
              "aria-expanded": isExpanded,
              onClick: !isIframeDisabled ? onHeaderClick : undefined,
              children: [!!badgeImage && /*#__PURE__*/jsxs("span", {
                className: badgeWrapperCls,
                children: [/*#__PURE__*/jsx("img", {
                  className: badgeImageCls,
                  src: badgeImage,
                  alt: "",
                  role: "presentation"
                }), !!props.indexOrLevel && /*#__PURE__*/jsx("span", {
                  className: cx(indexBadgeCls, audience),
                  children: props.indexOrLevel
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: headerWrapperCls,
                children: [/*#__PURE__*/jsx("div", {
                  className: cx(headerTextCls, scrambledCls, props.isScrambled && "scrambled"),
                  children: /*#__PURE__*/jsx("span", {
                    children: getCaptionValue(props.caption?.title) ?? props.learningObject?.title ?? props.learningObject?.id ?? "Indlejret indhold"
                  })
                }), hasDescription && !props.isScrambled && /*#__PURE__*/jsxs(Fragment, {
                  children: [/*#__PURE__*/jsx(SizeTransition, {
                    axis: "vertical",
                    timeout: CONTENT_TRANSITION_DURATION_MS,
                    in: !isExpanding,
                    classNames: descriptionWrapperCls,
                    mountOnEnter: true,
                    unmountOnExit: true,
                    children: ref => /*#__PURE__*/jsx("div", {
                      ref: ref,
                      className: descriptionWrapperCls,
                      children: /*#__PURE__*/jsx("span", {
                        className: descriptionCls,
                        children: props.richTextDescription
                      })
                    }, "description")
                  }), /*#__PURE__*/jsxs("div", {
                    className: cx(descriptionPanelWrapperCls, showDescription && "withDescription"),
                    children: [/*#__PURE__*/jsx(SizeTransition, {
                      axis: "vertical",
                      timeout: CONTENT_TRANSITION_DURATION_MS,
                      in: showDescription,
                      classNames: descriptionWrapperCls,
                      mountOnEnter: true,
                      unmountOnExit: true,
                      children: ref => /*#__PURE__*/jsx("div", {
                        ref: ref,
                        className: descriptionWrapperCls,
                        children: /*#__PURE__*/jsx("span", {
                          className: cx(descriptionCls, descriptionPanelCls),
                          children: props.richTextDescription
                        })
                      }, "description")
                    }), /*#__PURE__*/jsxs("button", {
                      className: descriptionToggleCls,
                      onClick: toggleDescription,
                      children: [!showDescription && "Se beskrivelse", showDescription && "Skjul beskrivelse"]
                    })]
                  })]
                })]
              }), /*#__PURE__*/jsx("div", {
                className: expandCls,
                children: /*#__PURE__*/jsxs("span", {
                  className: iconWrapperCls,
                  children: [/*#__PURE__*/jsx("span", {
                    className: chevronCls,
                    children: /*#__PURE__*/jsx(Chevron, {
                      vertical: true,
                      size: 12,
                      lineWidth: 2.5
                    })
                  }), /*#__PURE__*/jsx("span", {
                    className: crossCls,
                    onClick: onCloseClick,
                    children: /*#__PURE__*/jsx(CrossIcon, {
                      height: 18
                    })
                  })]
                })
              })]
            })
          })
        })
      })
    }), /*#__PURE__*/jsxs("div", {
      id: sectionId,
      "aria-labelledby": buttonId,
      className: cx(contentContainerCls, !isExpanding && "collapsed"),
      children: [/*#__PURE__*/jsx(CSSRefTransition, {
        in: showIframe && !!displayData?.src,
        classNames: fadeCls,
        timeout: FADE_TRANSITION_DURATION_MS,
        nodeRef: ref,
        unmountOnExit: true,
        mountOnEnter: true,
        children: ref => /*#__PURE__*/jsx("iframe", {
          ref: ref,
          className: iframeCls,
          title: getCaptionValue(props.caption?.title),
          src: window.gProxy.forwardTo(displayData?.src ?? ""),
          scrolling: "yes",
          onLoad: onIframeLoaded,
          allowFullScreen: true
        })
      }), /*#__PURE__*/jsx(CSSRefTransition, {
        in: showThrobber,
        classNames: fadeCls,
        timeout: FADE_TRANSITION_DURATION_MS,
        unmountOnExit: true,
        mountOnEnter: true,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: throbberCls,
          "aria-label": "Indlæser...",
          children: /*#__PURE__*/jsx(Throbber, {})
        })
      }), /*#__PURE__*/jsx(CSSRefTransition, {
        in: failedLoadingSrc,
        classNames: fadeCls,
        timeout: FADE_TRANSITION_DURATION_MS,
        unmountOnExit: true,
        mountOnEnter: true,
        children: ref => /*#__PURE__*/jsxs("div", {
          ref: ref,
          className: textCls,
          children: ["Der gik noget galt da vi fors\xF8gte at hente komponenten.", /*#__PURE__*/jsx("button", {
            className: retryButtonCls,
            onClick: retryFetchingSrc,
            children: "Klik her for at pr\xF8ve igen"
          })]
        })
      }), /*#__PURE__*/jsx(CSSRefTransition, {
        in: isIframeDisabled,
        classNames: fadeCls,
        timeout: FADE_TRANSITION_DURATION_MS,
        unmountOnExit: true,
        mountOnEnter: true,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: textCls,
          children: "Vi kan desv\xE6rre ikke give dig adgang til denne komponent."
        })
      })]
    }), /*#__PURE__*/jsx("div", {
      className: captionCls,
      children: /*#__PURE__*/jsxs("span", {
        className: scrambledCls,
        children: [props.caption?.caption?.showInContext && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: props.caption?.caption.value
          }
        }), " ", props.caption?.authorOrArtist?.showInContext && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: props.caption?.authorOrArtist.value
          }
        }), " ", props.caption?.copyright?.showInContext && props.caption?.copyright?.value && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: "©&nbsp;" + props.caption?.copyright.value
          }
        }), " ", props.caption?.year?.showInContext && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: props.caption?.year.value
          }
        }), " ", props.caption?.country?.showInContext && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: props.caption?.country.value
          }
        }), " ", props.caption?.duration?.showInContext && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: props.caption?.duration.value
          }
        }), " ", props.caption?.numberOfPages?.showInContext && /*#__PURE__*/jsx("span", {
          dangerouslySetInnerHTML: {
            __html: props.caption?.numberOfPages.value
          }
        })]
      })
    })]
  });
};

export { HarmonicaIframeComponent, HarmonicaIframeComponentSelector };
//# sourceMappingURL=comp.harmonica-iframe.js.map
